<template>
  <div>
    <h1 class="page-title text-left">Trap Site Master</h1>
    <div class="panel">
      <div class="panel-heading">
        <h3 class="panel-title">
          Details
          <router-link :to="{ name: 'data-trap-sites' }" class="pull-right ml-10"><i class="fa fa-list"></i></router-link>
          <i class="fa fa-pencil pointer pull-right" v-on:click="showEdit({id: id, list: true})"></i>
        </h3>
      </div>

      <loading :working="loading"></loading>

      <div class="panel-body" v-if="!loading">
        <dl class="dl-horizontal">
          <dt>Site Name or Id</dt>
          <dd>{{ siteName(form.data.siteId) }}</dd>

          <dt>Address or Physical Location</dt>
          <dd class="pointer" v-on:click="showMap(`${form.data.address}, ${form.data.city} ${stateName(form.data.stateId)} ${form.data.zip} `)">
            <i class="fa fa-map"></i>
            {{ form.data.address }}<br>
            {{ form.data.city }}, {{ stateName(form.data.stateId) }} {{ form.data.zip }}
          </dd>

          <dt>Zone</dt>
          <dd>{{ zoneName(form.data.zoneId) }}</dd>

          <dt>Municipality</dt>
          <dd>{{ municipalityName(form.data.municipalityId) }}</dd>

          <dt>Trap Name or Number</dt>
          <dd>{{ form.data.trapNumber }}</dd>

          <dt>Active</dt>
          <dd><i class="fa" :class="{ 'fa-check-square-o': form.data.isActive, 'fa-square-o': !form.data.isActive}"></i></dd>

          <dt>County</dt>
          <dd>{{ countyName(form.data.countyId) }}</dd>

          <dt>GPS Coordinates</dt>
          <dd class="pointer" v-on:click="showMap({lat: form.data.latitude, lon: form.data.longitude})"><i class="fa fa-map-marker"></i> {{ form.data.latitude }}, {{ form.data.longitude}}</dd>

          <dt>Altitude</dt>
          <dd>{{ form.data.altitude }}</dd>

          <dt>Trap Type</dt>
          <dd>{{ trapTypeName(form.data.trapTypeId) }}</dd>

          <dt>Trap Night</dt>
          <dd>{{ trapNightName(form.data.trapNightId) }}</dd>

          <dt>Trap Team</dt>
          <dd>{{ trapTeamName(form.data.trapTeamId) }}</dd>

          <dt>Schedule</dt>
          <dd>{{ form.data.scheduleStart | date }} every {{ form.data.scheduleFrequency }} days</dd>

          <dt>Site is Mandatory</dt>
          <dd><i class="fa" :class="{ 'fa-check-square-o': form.data.isMandatory, 'fa-square-o': !form.data.isMandatory}"></i></dd>

        </dl>

        <div class="mt-20 clearfix">

          <!-- Nav tabs -->
          <ul class="nav nav-tabs" role="tablist">
            <li role="presentation" class="active"><a href="#adult-surveillance" role="tab" data-toggle="tab">Adult Surveillance<i class="badge"></i></a></li>
            <li role="presentation"><a href="#comments" role="tab" data-toggle="tab">Comments</a></li>
          </ul>

          <!-- Tab panes -->
          <div class="tab-content">
            <div role="tabpanel" class="tab-pane active" id="adult-surveillance">

              <router-link :to="{ name: 'data-adult-surveillances-with', params: { type: 'ts', id: id } }" class="btn btn-primary pull-right">Add adult surveillance</router-link>

              <table class="table table-striped">
                <thead>
                  <tr>
                    <th>Trap Site</th>
                    <th>Trap Set</th>
                    <th>Date Processed</th>
                    <th>ID Initials</th>
                    <th>Mosquito Species</th>
                    <th>Mosquito Count</th>
                    <th>Sample Number</th>
                    <th>Tested</th>
                    <th>CDC Sample Number</th>
                    <th>Irregular Lab Results</th>
                    <th>Work Status</th>
                    <th width="120"></th>
                  </tr>
                </thead>
                <tbody>
                <tr v-for="adultSurveillance in form.data.adultSurveillances">
                  <td>{{ adultSurveillance.trapSite.trapNumber }}</td>
                  <td>{{ adultSurveillance.dateTrapSet | datetime }}</td>
                  <td>{{ adultSurveillance.dateProcessed | datetime }}</td>
                  <td>{{ adultSurveillance.idInitials }}</td>
                  <td>{{ mosquitoName(adultSurveillance.mosquitoSpeciesId) }}</td>
                  <td>{{ adultSurveillance.mosquitoCount }}</td>
                  <td>{{ adultSurveillance.sampleNumber }}</td>
                  <td><i class="fa" :class="{ 'fa-check-square-o': adultSurveillance.tested, 'fa-square-o': !adultSurveillance.tested}"></i></td>
                  <td>{{ adultSurveillance.cdcSampleNumber }}</td>
                  <td><i class="fa" :class="{ 'fa-check-square-o': adultSurveillance.irregularLabResults, 'fa-square-o': !adultSurveillance.irregularLabResults}"></i></td>
                  <td>{{ adultSurveillance.workStatus }}</td>
                  <td>
                    <router-link :to="{ name: 'data-adult-surveillance-details', params: { id: adultSurveillance.id }}"><i class="fa fa-ellipsis-h" style="margin-right: 10px;"></i></router-link>
                  </td>
                </tr>
                </tbody>
              </table>

            </div>

            <div role="tabpanel" class="tab-pane" id="comments">
              <table class="table table-striped">
                <thead>
                <th>Date</th>
                <th>By</th>
                <th>Comment</th>
                </thead>
                <tbody>
                <tr v-for="comment in form.data.comments">
                  <td>{{ comment.updatedAt | date }}</td>
                  <td>{{ comment.user.firstName }} {{ comment.user.lastName }}</td>
                  <td>{{ comment.body }}</td>
                </tr>
                </tbody>
              </table>

            </div>
          </div>

        </div>

      </div>


    </div>

    <trap-site-form v-if="showForm" :form="form"></trap-site-form>

    <google-map ref="gmap"></google-map>

  </div>
</template>

<script>
  import TrapSitesService from '@/services/TrapSitesService';
  import Form from '@/utils/form';
  import loader from '@/utils/loader';
  import trapSiteForm from '@/components/forms/trap_site_details';
  import googleMap from '@/components/googleMap';
  import loading from '@/components/loading';

  import store from '@/store';

  export default {
    name: 'trap_site_details',

    components: {
      trapSiteForm,
      googleMap,
      loading
    },

    props: ['id'],

    data() {
      return {
        loading: true,
        showForm: false,
        form: new Form(
          TrapSitesService,
          {})
      }
    },

    methods: {
      siteName (id) {
        return this.$store.getters.siteNameById(id);
      },
      countyName(id) {
        return this.$store.getters.countyNameById(id);
      },
      municipalityName(id) {
        return this.$store.getters.municipalityNameById(id);
      },
      trapTeamName(id) {
        return this.$store.getters.trapTeamNameById(id);
      },
      trapTypeName(id) {
        return this.$store.getters.trapTypeNameById(id);
      },
      trapNightName(id) {
        return this.$store.getters.trapNightNameById(id);
      },
      zoneName(id) {
        return this.$store.getters.zoneNameById(id);
      },
      mosquitoName(id) {
        return this.$store.getters.mosquitoNameById(id);
      },
      stateName(id) {
        return this.$store.getters.stateNameById(id);
      },
      showMap(coordinates) {
        this.$refs.gmap.showMap(coordinates);
      },
      showEdit(params) {
        this.showForm = true;
        this.form.showEdit(params);
      }
    },

    mounted() {

      loader.lookups()
        .then(() => {
          this.form.getDetails(this.id)
            .then(() => {
              this.loading = false;
              this.form.siteSearch.term = this.siteName(this.form.data.siteId);
              this.form.onSiteSearch('siteId');
            });
        });
    }

  }
</script>

<style lang="scss">

</style>
